// The labelled input primitive (ADR-0047 UI-09, ported to Material 3 in
// ADR-0051 Stage 4).
//
// This used to be a hand-rolled floating label: a real <label for> over a bare
// <input>, floated on `:focus` or `:not(:placeholder-shown)` in pure CSS. It
// existed because the composer's placeholder-only inputs had no accessible
// name and lost their only description at the first keystroke (ADR-0046).
// `md-outlined-text-field` does exactly that job, to M3 spec, so the hand-
// rolled version is retired and this is now a thin adapter over it.
//
// The adapter is kept (rather than calling `ce('md-outlined-text-field')` at
// every site) for two reasons: the option names stay ours, and `root`/`input`
// keep their old meaning for callers. In Material both ARE the same element —
// the real <input> lives in its shadow root — so specs reach the value through
// `#the-id input`; that is what the E2E helpers do.
//
// Locale-agnostic: labels arrive as data, `t()` is applied at the call site
// (docs/DESIGN-GUIDE.md).

import '@material/web/textfield/outlined-text-field.js';

import { ce } from '../dom.js';

export interface FieldOptions {
  /** Required: the element id, and the handle for E2E specs. */
  id: string;
  label: string;
  type?: string;
  value?: string;
  /** Shown only while focused — a hint, never the label (ADR-0046). */
  placeholder?: string;
  /** Trailing unit inside the control, e.g. "min". */
  suffix?: string;
  /** Persistent helper text under the field. */
  hint?: string;
  /** Validation message; also puts the field in its error state. */
  error?: string;
  /** Passed straight through (min, max, step, inputmode, autocomplete…). */
  attrs?: Record<string, string>;
}

/** What callers actually use off a text field. */
export type FieldControl = HTMLElement & {
  value: string;
  select(): void;
  focus(): void;
};

export interface Field {
  root: FieldControl;
  /** The same element as `root` — Material owns the inner <input>. */
  input: FieldControl;
}

export function field(opts: FieldOptions): Field {
  const control = ce('md-outlined-text-field', {
    id: opts.id,
    label: opts.label,
    type: opts.type ?? 'text',
    ...(opts.value !== undefined ? { value: opts.value } : {}),
    ...(opts.placeholder ? { placeholder: opts.placeholder } : {}),
    ...(opts.suffix ? { 'suffix-text': opts.suffix } : {}),
    ...(opts.hint ? { 'supporting-text': opts.hint } : {}),
    ...(opts.error ? { error: true, 'error-text': opts.error } : {}),
    ...(opts.attrs ?? {}),
  }) as FieldControl;

  return { root: control, input: control };
}
